import { PendingInvite } from '@/types';
import axios from 'axios';
import { useWallet } from 'solana-wallets-vue';
import { Keypair, PublicKey, Transaction } from '@solana/web3.js';
import { getConnection } from '@/helpers/solana/getConnection';
import { generateInviteCode } from '@/helpers/generateInviteCode';
import { sha256 } from 'js-sha256';
import { solanaFactor } from '@/constants';
import { useDataStore } from '@/store/data';
import { Buffer } from 'buffer';
import dayjs from 'dayjs';

export default function useInvitation() {
  const { sendTransaction, publicKey } = useWallet();
  const dataStore = useDataStore();
  const INVITE_CODE_PRIVATE_KEY_PREFIX = 'invite:';
  const INVITE_CODE_PDA_SEED = 'invite';
  const INVITE_ESCROW_PROGRAM = 'inv1tEtSwRMtM44tbvJGNiTxMvDfPVnX9StyqXfDfks';

  function toKeyPair(codeBase58: string): Keypair {
    const seedString = `${INVITE_CODE_PRIVATE_KEY_PREFIX}${codeBase58}`;
    const seedHash = sha256.digest(seedString);
    const seed = Uint8Array.from(seedHash);
    return Keypair.fromSeed(seed.slice(0, 32));
  }

  const create = async (amount: number): Promise<PendingInvite> => {
    if (!publicKey.value) throw new Error('Connect Wallet first');
    const inviteCode = await generateInviteCode();
    const inviteKey = toKeyPair(inviteCode);
    const programDerivedAddress = PublicKey.findProgramAddressSync(
      [Buffer.from(INVITE_CODE_PDA_SEED), inviteKey.publicKey.toBytes()],
      new PublicKey(INVITE_ESCROW_PROGRAM),
    )[0];

    const [encodedUnsignedTransaction, expiry] = await axios
      .post(`https://invite-api.jup.ag/post/craft-send`, {
        inviteSigner: inviteKey.publicKey.toString(),
        sender: publicKey.value.toString(),
        amount: `${amount * solanaFactor}`,
      })
      .then((res) => [res.data.tx, res.data.expiry]);

    const unsignedTransaction = Transaction.from(
      Buffer.from(encodedUnsignedTransaction, 'base64'),
    );

    unsignedTransaction.partialSign(inviteKey);
    const signature = await sendTransaction(
      unsignedTransaction,
      getConnection(),
    );

    await axios.post(`https://invite-api.jup.ag/post/send`, {
      inviteSigner: inviteKey.publicKey.toString(),
      invitePDA: programDerivedAddress.toString(),
      sender: publicKey.value.toString(),
      expiry,
      amount: `${amount * solanaFactor}`,
      creationTx: signature,
    });

    dataStore.setInviteCode(programDerivedAddress.toString(), inviteCode);

    return {
      amount: `${amount * solanaFactor}`,
      code: inviteCode,
      creation_time: dayjs().unix().toString(),
      expiry,
      creation_tx: signature,
      invite_signer: inviteKey.publicKey.toString(),
      invite_pda: programDerivedAddress.toString(),
      sender: publicKey.value.toString(),
      mode: 'creation',
    } satisfies PendingInvite;
  };

  const clawback = async (pendingInvite: PendingInvite) => {
    const encodedUnsignedTransaction = await axios
      .post(`https://invite-api.jup.ag/post/craft-clawback`, {
        invitePDA: pendingInvite.invite_pda,
        sender: pendingInvite.sender,
      })
      .then((res) => res.data.tx);

    const unsignedTransaction = Transaction.from(
      Buffer.from(encodedUnsignedTransaction, 'base64'),
    );

    const signature = await sendTransaction(
      unsignedTransaction,
      getConnection(),
    );

    await axios.post(`https://invite-api.jup.ag/post/clawback`, {
      invitePDA: pendingInvite.invite_pda,
      sender: pendingInvite.sender,
      signature,
    });
  };

  return {
    create,
    clawback,
  };
}
